import React, { useState } from "react";
import {
  EuiButton,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiPopover,
  EuiConfirmModal,
  EuiGlobalToastList,
  EuiSwitch,
  EuiSpacer,
} from "@elastic/eui";
import { useMutation } from "@apollo/client";

import BULK_TRANSLATE_RECIPES from "../mutations/bulkTranslateRecipes";

// Must match LANGUAGE_TO_INDEX in server/core/graphql/mutations.py.
const LANGUAGES = [
  { code: "ES", label: "Spanish", targetIndex: "recipes-spanish" },
  { code: "EN-GB", label: "English (United Kingdom)", targetIndex: "recipes-en-gb" },
  { code: "NL", label: "Dutch", targetIndex: "recipes-dutch" },
  { code: "AR", label: "Arabic", targetIndex: "recipes-arabic" },
  { code: "IT", label: "Italian", targetIndex: "recipes-it" },
];

export default function BulkTranslateRecipesAction({ recipeIds }) {
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState(null);
  const [skipIfTranslated, setSkipIfTranslated] = useState(true);
  const [toasts, setToasts] = useState([]);

  const [bulkTranslate, { loading }] = useMutation(BULK_TRANSLATE_RECIPES, {
    errorPolicy: "all",
    onCompleted(data) {
      const r = data && data.bulkTranslateRecipes;
      if (!r) return;
      setToasts((prev) => [
        ...prev,
        {
          id: `bulk-translate-${r.taskId}`,
          title: `Translation kicked off (${r.language})`,
          color: "success",
          iconType: "check",
          text: (
            <span>
              Queued <b>{r.queuedCount}</b> recipe(s) to{" "}
              <code>{r.targetIndex}</code>.
              <br />
              Task ID: <code>{r.taskId}</code>
            </span>
          ),
        },
      ]);
    },
    onError(error) {
      setToasts((prev) => [
        ...prev,
        {
          id: `bulk-translate-err-${Date.now()}`,
          title: "Translation failed to start",
          color: "danger",
          iconType: "alert",
          text: error.message,
        },
      ]);
    },
  });

  const openPopover = () => setPopoverOpen(true);
  const closePopover = () => setPopoverOpen(false);

  const onPickLanguage = (lang) => {
    closePopover();
    setSkipIfTranslated(true);
    setPendingLanguage(lang);
  };

  const onCancelConfirm = () => setPendingLanguage(null);

  const onConfirm = () => {
    const lang = pendingLanguage;
    const skip = skipIfTranslated;
    setPendingLanguage(null);
    if (!lang) return;
    bulkTranslate({
      variables: {
        input: {
          recipeIds,
          language: lang.code,
          skipIfTranslated: skip,
        },
      },
    });
  };

  const removeToast = (toast) =>
    setToasts((prev) => prev.filter((t) => t.id !== toast.id));

  const disabled = !recipeIds || recipeIds.length === 0;

  const button = (
    <EuiButton
      iconType="globe"
      iconSide="right"
      onClick={openPopover}
      isLoading={loading}
      disabled={disabled || loading}
      fill
    >
      Translate selected ({recipeIds ? recipeIds.length : 0})
    </EuiButton>
  );

  const items = LANGUAGES.map((lang) => (
    <EuiContextMenuItem
      key={lang.code}
      icon="quote"
      onClick={() => onPickLanguage(lang)}
    >
      {lang.label} ({lang.code})
    </EuiContextMenuItem>
  ));

  return (
    <>
      <EuiPopover
        id="bulk-translate-popover"
        button={button}
        isOpen={isPopoverOpen}
        closePopover={closePopover}
        panelPaddingSize="none"
        anchorPosition="downLeft"
      >
        <EuiContextMenuPanel size="m" items={items} />
      </EuiPopover>

      {pendingLanguage && (
        <EuiConfirmModal
          title="Translate selected recipes?"
          onCancel={onCancelConfirm}
          onConfirm={onConfirm}
          cancelButtonText="Cancel"
          confirmButtonText="Translate"
          defaultFocusedButton="confirm"
        >
          <p>
            About to translate <b>{recipeIds.length}</b> recipe(s) to{" "}
            <b>{pendingLanguage.label}</b> (<code>{pendingLanguage.code}</code>).
          </p>
          <p>
            Target index: <code>{pendingLanguage.targetIndex}</code>
          </p>
          <EuiSwitch
            label={`Skip recipes already translated to ${pendingLanguage.label}`}
            checked={skipIfTranslated}
            onChange={(e) => setSkipIfTranslated(e.target.checked)}
          />
          <EuiSpacer size="s" />
          <p>
            {skipIfTranslated ? (
              <>
                Recipes already present in <code>{pendingLanguage.targetIndex}</code>{" "}
                will be skipped (no GPT call, no overwrite).
              </>
            ) : (
              <>
                <b>All selected recipes will be re-translated</b> and existing
                translations in <code>{pendingLanguage.targetIndex}</code> will
                be overwritten.
              </>
            )}
          </p>
          <p>
            Translation runs in the background. You will get a toast with the
            task id once it has been queued.
          </p>
        </EuiConfirmModal>
      )}

      <EuiGlobalToastList
        toasts={toasts}
        dismissToast={removeToast}
        toastLifeTimeMs={8000}
      />
    </>
  );
}
